import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Typography,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    Grid,
    Paper,
    LinearProgress,
    TextField,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogContent,
    ListItemIcon,
    ListItemText,
    Divider,
} from '@mui/material';
import {
    People,
    LocationCity,
    HowToVote,
    Groups,
    SupervisedUserCircle,
    Report,
    AccountBalanceWallet,
    Map,
    TrendingUp,
    PieChart as PieChartIcon,
    Assessment,
    MoreVert,
    OpenInFull,
    Refresh,
    DateRange,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/axios';
import {
    PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
    BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';

interface StatCardProps {
    title: string;
    value: number | string;
    icon: React.ReactElement;
    color: string;
    bgColor: string;
    onClick?: () => void;
    subtitle?: string;
}

const StatCard = ({ title, value, icon, color, bgColor, onClick, subtitle }: StatCardProps) => {
    return (
        <Card
            sx={{
                height: '100%',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRadius: '12px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.12)',
                border: '1px solid rgba(0,0,0,0.04)',
                position: 'relative',
                overflow: 'hidden',
                bgcolor: '#ffffff',
                borderLeft: `6px solid ${color}`,
                '&:hover': onClick ? {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12), 0 20px 48px rgba(0,0,0,0.15)',
                    borderLeft: `6px solid ${color}`,
                } : {},
            }}
            onClick={onClick}
        >
            <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography
                        color="text.secondary"
                        variant="body2"
                        sx={{
                            fontSize: '0.85rem',
                            fontWeight: 500,
                            color: '#6b7280'
                        }}
                    >
                        {title}
                    </Typography>
                    <Box
                        sx={{
                            bgcolor: bgColor,
                            color: color,
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {React.cloneElement(icon as React.ReactElement<any>, { sx: { fontSize: 20 } })}
                    </Box>
                </Box>
                <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{
                        color: '#1a1a1a',
                        letterSpacing: '-0.5px',
                        fontSize: '1.75rem',
                        mt: 1
                    }}
                >
                    {value}
                </Typography>
            </CardContent>
        </Card>
    );
};

const Dashboard = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { user, hasPermission } = useAuth();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [selectedMonth, setSelectedMonth] = React.useState(new Date().toISOString().slice(0, 7));

    // State for Citizen Request Status Distribution card
    const [pieMenuAnchor, setPieMenuAnchor] = React.useState<null | HTMLElement>(null);
    const [pieFullscreen, setPieFullscreen] = React.useState(false);
    const [pieTimeFilter, setPieTimeFilter] = React.useState<'all' | '7' | '15' | '30'>('all');

    // State for Daily New Citizen Requests card
    const [barMenuAnchor, setBarMenuAnchor] = React.useState<null | HTMLElement>(null);
    const [barFullscreen, setBarFullscreen] = React.useState(false);

    // Fetch basic counts and wallet
    const { data: overviewData, isLoading: isOverviewLoading } = useQuery({
        queryKey: ['tenant-dashboard-overview'],
        queryFn: async () => {
            const res = await api.get('/tenant/dashboard');
            return res.data?.data || {};
        },
    });

    const counts = overviewData?.counts || {};
    const wallet = overviewData?.wallet || {};

    const { data: complaintStats, isLoading: isComplaintStatsLoading } = useQuery({
        queryKey: ['tenant-dashboard-complaint-stats'],
        queryFn: async () => {
            const res = await api.get('/tenant/dashboard/complaint-stats');
            return res.data?.data || {};
        },
    });

    const { data: dailyStats = [], isLoading: isDailyStatsLoading } = useQuery({
        queryKey: ['tenant-dashboard-daily-stats', selectedMonth],
        queryFn: async () => {
            const [year, month] = selectedMonth.split('-');
            const res = await api.get('/tenant/dashboard/daily-stats', {
                params: { year, month }
            });
            return res.data?.data || [];
        },
    });

    const { data: ageDistribution = [], isLoading: isAgeDistributionLoading } = useQuery({
        queryKey: ['tenant-dashboard-age-distribution'],
        queryFn: async () => {
            const res = await api.get('/tenant/dashboard/age-distribution');
            return res.data?.data || [];
        },
    });

    const { data: proPartyDistribution = [], isLoading: isProPartyDistributionLoading } = useQuery({
        queryKey: ['tenant-dashboard-pro-party-distribution'],
        queryFn: async () => {
            const res = await api.get('/tenant/dashboard/pro-party-distribution');
            return res.data?.data || [];
        },
    });

    const { data: communityDistribution = [], isLoading: isCommunityDistributionLoading } = useQuery({
        queryKey: ['tenant-dashboard-community-distribution'],
        queryFn: async () => {
            const res = await api.get('/tenant/dashboard/community-distribution');
            return res.data?.data || [];
        },
    });

    const isLoading = isOverviewLoading;

    // Prepare data for Pie Chart
    const pieData = [
        { name: 'Pending', value: complaintStats?.pending || 0, color: '#FFB020' }, // Warning/Orange
        { name: 'In Progress', value: complaintStats?.in_progress || 0, color: '#1976D2' }, // Info/Blue
        { name: 'Resolved', value: complaintStats?.resolved || 0, color: '#2E7D32' }, // Success/Green
        { name: 'Rejected', value: complaintStats?.rejected || 0, color: '#D32F2F' }, // Error/Red
    ];

    const hasComplaintData = pieData.some(d => d.value > 0);

    const proPartyFallbackColors = ['#1976D2', '#D32F2F', '#388E3C', '#FBC02D', '#7B1FA2', '#E64A19', '#0288D1'];
    const proPartyData = proPartyDistribution?.map((item: any, index: number) => ({
        ...item,
        color: item.color || proPartyFallbackColors[index % proPartyFallbackColors.length]
    })) || [];
    const hasProPartyData = proPartyData.some((d: any) => d.value > 0);

    const communityColors = ['#2E7D32', '#1565C0', '#E65100', '#6A1B9A', '#00838F', '#C62828', '#F9A825', '#4527A0', '#00695C', '#AD1457'];
    const communityData = communityDistribution?.map((item: any, index: number) => ({
        ...item,
        color: communityColors[index % communityColors.length]
    })) || [];
    const hasCommunityData = communityData.some((d: any) => d.value > 0);

    // Calculate total for percentage calculation
    const pieTotal = pieData.reduce((sum, item) => sum + item.value, 0);

    // Custom label renderer for pie chart - shows percentage inside segments
    const renderCustomLabel = ({
        cx,
        cy,
        midAngle,
        innerRadius,
        outerRadius,
        percent,
    }: any) => {
        if ((percent ?? 0) < 0.05) return null; // Don't show label if less than 5%
        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text
                x={x}
                y={y}
                fill="white"
                textAnchor="middle"
                dominantBaseline="central"
                style={{ fontWeight: 700, fontSize: '12px', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
            >
                {`${((percent ?? 0) * 100).toFixed(0)}%`}
            </text>
        );
    };

    const stats = [
        {
            title: 'Total Users',
            value: counts.users || 0,
            icon: <People />,
            color: '#7B4DFF', // Purple
            bgColor: '#F3EFFF',
            onClick: () => navigate('/admin/users'),
        },
        {
            title: 'Citizens',
            value: counts.citizens || 0,
            icon: <Groups />,
            color: '#00C2FF', // Blue
            bgColor: '#E5F9FF',
            onClick: () => navigate('/admin/all-citizens'),
        },
        {
            title: 'Villages',
            value: counts.villages || 0,
            icon: <LocationCity />,
            color: '#FFB800', // Yellow
            bgColor: '#FFF8E1',
            onClick: () => navigate('/admin/citizens'), // Navigate to main hierarchy page
        },
        {
            title: 'Voting Booths',
            value: counts.voting_booths || 0,
            icon: <HowToVote />,
            color: '#00BFA5', // Teal
            bgColor: '#E0F2F1',
            onClick: () => navigate('/admin/citizens'), // Navigate to main hierarchy page
        },
        {
            title: 'Sub Leaders',
            value: counts.sub_leaders || 0,
            icon: <SupervisedUserCircle />,
            color: '#FF6B35', // Orange
            bgColor: '#FFF3ED',
            onClick: () => navigate('/admin/sub-leaders'),
        },
    ];

    if (isLoading) {
        return (
            <Box sx={{ width: '100%', mt: 4 }}>
                <LinearProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h1" fontWeight={700} gutterBottom sx={{ color: '#1a1a1a' }}>
                        Dashboard
                    </Typography>
                    <Typography color='#3f3f4699' variant="body1">
                        Overview of your admin analytics and activities
                    </Typography>
                </Box>
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Typography variant="body2" sx={{ bgcolor: 'white', py: 1, px: 2, borderRadius: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.12)', border: '1px solid rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUp fontSize="small" color="success" />
                        System Status: <Chip label="Online" color="success" />
                    </Typography>
                </Box>
            </Box>

            {/* Stats Grid */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                        lg: 'repeat(5, 1fr)'
                    },
                    gap: 3,
                    mb: 4,
                }}
            >
                {stats.map((stat, index) => (
                    <StatCard key={index} {...stat} />
                ))}
            </Box>

            {/* Charts & Graphs Section */}
            <Grid container spacing={3}>

                {/* Daily Citizen Requests Trend */}
                <Grid size={{ xs: 8 }}>
                    <Paper
                        sx={{
                            borderRadius: 4,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.12)',
                            border: '1px solid rgba(0,0,0,0.04)',
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 3,
                            py: 2,
                            bgcolor: '#f8f9fa',
                            borderBottom: '1px solid #eee'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Assessment sx={{ color: '#7B4DFF', fontSize: 22 }} />
                                <Typography variant="body1" fontWeight={600} sx={{ color: '#1a1a1a' }}>Daily New Citizen Requests</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField
                                    type="month"
                                    size="small"
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                    sx={{ width: 160 }}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => setBarFullscreen(true)}
                                    sx={{ color: 'text.secondary' }}
                                >
                                    <OpenInFull fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={(e) => setBarMenuAnchor(e.currentTarget)}
                                    sx={{ color: 'text.secondary' }}
                                >
                                    <MoreVert fontSize="small" />
                                </IconButton>
                                <Menu
                                    anchorEl={barMenuAnchor}
                                    open={Boolean(barMenuAnchor)}
                                    onClose={() => setBarMenuAnchor(null)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                    PaperProps={{
                                        sx: { borderRadius: 2, minWidth: 160, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }
                                    }}
                                >
                                    <MenuItem onClick={() => { setBarMenuAnchor(null); }}>
                                        <ListItemIcon><Refresh fontSize="small" /></ListItemIcon>
                                        <ListItemText>Refresh</ListItemText>
                                    </MenuItem>
                                </Menu>
                            </Box>
                        </Box>

                        <Box sx={{ width: '100%', height: 350, p: 2 }}>
                            {isDailyStatsLoading ? (
                                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <LinearProgress sx={{ width: '50%' }} />
                                </Box>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={dailyStats}
                                        margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5,
                                        }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                        <XAxis
                                            dataKey="day"
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: '#666' }}
                                        />
                                        <YAxis
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: '#666' }}
                                        />
                                        <Tooltip
                                            cursor={{ fill: '#f5f5f5' }}
                                            contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                                        />
                                        <Bar
                                            dataKey="count"
                                            name="Requests"
                                            fill="#7B4DFF"
                                            radius={[4, 4, 0, 0]}
                                            barSize={20}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </Box>
                    </Paper>
                </Grid>


                {/* Citizen Request Status Distribution */}
                {hasPermission('complaints.view') && (
                    <Grid size={{ xs: 4 }}>
                        <Paper
                            sx={{
                                borderRadius: 4,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.12)',
                                border: '1px solid rgba(0,0,0,0.04)',
                                height: '100%',
                                minHeight: 400,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden'
                            }}
                        >
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 3,
                                py: 2,
                                bgcolor: '#f8f9fa',
                                borderBottom: '1px solid #eee'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PieChartIcon sx={{ color: '#1976D2', fontSize: 22 }} />
                                    <Typography variant="body1" fontWeight={600} sx={{ color: '#1a1a1a' }}>Citizen Request Status Distribution</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => setPieFullscreen(true)}
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        <OpenInFull fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => setPieMenuAnchor(e.currentTarget)}
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        <MoreVert fontSize="small" />
                                    </IconButton>
                                    <Menu
                                        anchorEl={pieMenuAnchor}
                                        open={Boolean(pieMenuAnchor)}
                                        onClose={() => setPieMenuAnchor(null)}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                        PaperProps={{
                                            sx: { borderRadius: 2, minWidth: 160, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }
                                        }}
                                    >
                                        <MenuItem onClick={() => { setPieMenuAnchor(null); }}>
                                            <ListItemIcon><Refresh fontSize="small" /></ListItemIcon>
                                            <ListItemText>Refresh</ListItemText>
                                        </MenuItem>
                                        <Divider />
                                        <MenuItem
                                            onClick={() => { setPieTimeFilter('all'); setPieMenuAnchor(null); }}
                                            selected={pieTimeFilter === 'all'}
                                        >
                                            <ListItemIcon><DateRange fontSize="small" /></ListItemIcon>
                                            <ListItemText>All</ListItemText>
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() => { setPieTimeFilter('7'); setPieMenuAnchor(null); }}
                                            selected={pieTimeFilter === '7'}
                                        >
                                            <ListItemIcon><DateRange fontSize="small" /></ListItemIcon>
                                            <ListItemText>Last 7 Days</ListItemText>
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() => { setPieTimeFilter('15'); setPieMenuAnchor(null); }}
                                            selected={pieTimeFilter === '15'}
                                        >
                                            <ListItemIcon><DateRange fontSize="small" /></ListItemIcon>
                                            <ListItemText>Last 15 Days</ListItemText>
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() => { setPieTimeFilter('30'); setPieMenuAnchor(null); }}
                                            selected={pieTimeFilter === '30'}
                                        >
                                            <ListItemIcon><DateRange fontSize="small" /></ListItemIcon>
                                            <ListItemText>Last 30 Days</ListItemText>
                                        </MenuItem>
                                    </Menu>
                                </Box>
                            </Box>

                            <Box sx={{ flex: 1, width: '100%', height: '100%', minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                                {isComplaintStatsLoading ? (
                                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <LinearProgress sx={{ width: '50%' }} />
                                    </Box>
                                ) : hasComplaintData ? (
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={pieData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={100}
                                                paddingAngle={3}
                                                dataKey="value"
                                                label={renderCustomLabel}
                                                labelLine={false}
                                            >
                                                {pieData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                                                formatter={(value: any) => [`${value} Requests`, 'Count']}
                                            />
                                            <Legend
                                                verticalAlign="bottom"
                                                align="center"
                                                layout="horizontal"
                                                iconType="circle"
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                        <Report sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                                        <Typography>No citizen request data available yet</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                )}

                {/* Fullscreen Dialog for Pie Chart */}
                <Dialog
                    open={pieFullscreen}
                    onClose={() => setPieFullscreen(false)}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{
                        sx: { borderRadius: 4, p: 2 }
                    }}
                >
                    <DialogContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                            <Typography variant="h5" fontWeight={600}>Citizen Request Status Distribution</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {pieTimeFilter !== 'all' && (
                                    <Chip
                                        label={`Last ${pieTimeFilter} Days`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                                <IconButton onClick={() => setPieFullscreen(false)}>
                                    <OpenInFull />
                                </IconButton>
                            </Box>
                        </Box>
                        <Box sx={{ width: '100%', height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {hasComplaintData ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={120}
                                            outerRadius={180}
                                            paddingAngle={3}
                                            dataKey="value"
                                            label={renderCustomLabel}
                                            labelLine={false}
                                        >
                                            {pieData.map((entry, index) => (
                                                <Cell key={`cell-fs-${index}`} fill={entry.color} stroke="none" />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                                            formatter={(value: any) => [`${value} Requests`, 'Count']}
                                        />
                                        <Legend
                                            verticalAlign="bottom"
                                            align="center"
                                            layout="horizontal"
                                            iconType="circle"
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                    <Report sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                                    <Typography variant="h6">No citizen request data available yet</Typography>
                                </Box>
                            )}
                        </Box>
                    </DialogContent>
                </Dialog>

                {/* Fullscreen Dialog for Bar Chart */}
                <Dialog
                    open={barFullscreen}
                    onClose={() => setBarFullscreen(false)}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{
                        sx: { borderRadius: 4, p: 2 }
                    }}
                >
                    <DialogContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                            <Typography variant="h5" fontWeight={600}>Daily New Citizen Requests</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField
                                    type="month"
                                    size="small"
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                    sx={{ width: 160 }}
                                />
                                <IconButton onClick={() => setBarFullscreen(false)}>
                                    <OpenInFull />
                                </IconButton>
                            </Box>
                        </Box>
                        <Box sx={{ width: '100%', height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={dailyStats}
                                    margin={{
                                        top: 5,
                                        right: 30,
                                        left: 20,
                                        bottom: 5,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                    <XAxis
                                        dataKey="day"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#666' }}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#666' }}
                                    />
                                    <Tooltip
                                        cursor={{ fill: '#f5f5f5' }}
                                        contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                                    />
                                    <Bar
                                        dataKey="count"
                                        name="Requests"
                                        fill="#7B4DFF"
                                        radius={[4, 4, 0, 0]}
                                        barSize={30}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </DialogContent>
                </Dialog>

                {/* Age Distribution (18+) */}
                {hasPermission('citizens.view') && (
                    <Grid size={{ xs: 8 }}>
                        <Paper
                            sx={{
                                borderRadius: 4,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.12)',
                                border: '1px solid rgba(0,0,0,0.04)',
                                overflow: 'hidden'
                            }}
                        >
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                px: 3,
                                py: 2,
                                bgcolor: '#f8f9fa',
                                borderBottom: '1px solid #eee'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Groups sx={{ color: '#00C2FF', fontSize: 22 }} />
                                    <Typography variant="body1" fontWeight={600} sx={{ color: '#1a1a1a' }}>Age Distribution (18+)</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ width: '100%', height: 350, p: 2 }}>
                                {isAgeDistributionLoading ? (
                                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <LinearProgress sx={{ width: '50%' }} />
                                    </Box>
                                ) : ageDistribution.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={ageDistribution}
                                            margin={{ top: 5, right: 30, left: 20, bottom: 5, }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                            <XAxis dataKey="range" axisLine={false} tickLine={false} tick={{ fill: '#666' }} />
                                            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#666' }} />
                                            <Tooltip cursor={{ fill: '#f5f5f5' }} contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                                            <Bar dataKey="count" name="Citizens" fill="#00C2FF" radius={[4, 4, 0, 0]} barSize={40} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                                        <Groups sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                                        <Typography>No age distribution data available</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                )}

                {/* Pro Party Distribution */}
                {hasPermission('citizens.view') && (
                    <Grid size={{ xs: 4 }}>
                        <Paper
                            sx={{
                                borderRadius: 4,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.12)',
                                border: '1px solid rgba(0,0,0,0.04)',
                                height: '100%',
                                minHeight: 400,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden'
                            }}
                        >
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 3,
                                py: 2,
                                bgcolor: '#f8f9fa',
                                borderBottom: '1px solid #eee'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PieChartIcon sx={{ color: '#D32F2F', fontSize: 22 }} />
                                    <Typography variant="body1" fontWeight={600} sx={{ color: '#1a1a1a' }}>Pro Party Distribution</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ flex: 1, width: '100%', height: '100%', minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                                {isProPartyDistributionLoading ? (
                                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <LinearProgress sx={{ width: '50%' }} />
                                    </Box>
                                ) : hasProPartyData ? (
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={proPartyData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={100}
                                                paddingAngle={3}
                                                dataKey="value"
                                                label={renderCustomLabel}
                                                labelLine={false}
                                            >
                                                {proPartyData.map((entry: any, index: number) => (
                                                    <Cell key={`cell-proparty-${index}`} fill={entry.color} stroke="none" />
                                                ))}
                                            </Pie>
                                            <Tooltip contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} formatter={(value: any) => [`${value} Citizens`, 'Count']} />
                                            <Legend verticalAlign="bottom" align="center" layout="horizontal" iconType="circle" />
                                        </PieChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                        <PieChartIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                                        <Typography>No pro-party data available yet</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                )}

                {/* Community Distribution */}
                {hasPermission('citizens.view') && (
                    <Grid size={{ xs: 12 }}>
                        <Paper
                            sx={{
                                borderRadius: 4,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.12)',
                                border: '1px solid rgba(0,0,0,0.04)',
                                overflow: 'hidden'
                            }}
                        >
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                px: 3,
                                py: 2,
                                bgcolor: '#f8f9fa',
                                borderBottom: '1px solid #eee'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Groups sx={{ color: '#2E7D32', fontSize: 22 }} />
                                    <Typography variant="body1" fontWeight={600} sx={{ color: '#1a1a1a' }}>Community Distribution</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ width: '100%', height: 350, p: 2 }}>
                                {isCommunityDistributionLoading ? (
                                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <LinearProgress sx={{ width: '50%' }} />
                                    </Box>
                                ) : hasCommunityData ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={communityData}
                                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#666', fontSize: 12 }} />
                                            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#666' }} />
                                            <Tooltip cursor={{ fill: '#f5f5f5' }} contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                                            <Bar dataKey="value" name="Citizens" fill="#2E7D32" radius={[4, 4, 0, 0]} barSize={40}>
                                                {communityData.map((entry: any, index: number) => (
                                                    <Cell key={`cell-community-${index}`} fill={entry.color} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                                        <Groups sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                                        <Typography>No community data available</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </Box >
    );
};

export default Dashboard;
